import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/nova/components';

import type { KitLayer, KitLayerRole, KitLayerText } from '../../banner-kit-api';
import * as S from './KitLayerEditor.styles';

/** A layer role's owner-facing name. Pure. */
export const layerRoleLabel = (role: KitLayerRole, t: TFunction): string => {
  switch (role) {
    case 'headline':
      return t('Headline');
    case 'subhead':
      return t('Sub-line');
    case 'cta':
      return t('Call to action');
    case 'logo':
      return t('Logo');
    case 'subject':
      return t('Main subject');
    default:
      return t('Decoration');
  }
};

/** Why a text layer's badge shows, in plain words, or undefined when its words read back fine (or it is no text
 *  layer). A layer the check read differently names what it read; one the check could not read at all (the read did
 *  not run, or found no words) says so. Derived from the stored read, so layers saved before this existed read the
 *  same. Pure. */
export const layerTextIssue = (text: KitLayerText | undefined, t: TFunction): string | undefined => {
  if (!text?.intended || text.verified !== false) return undefined;
  const read = text.read?.trim() ?? '';
  return read
    ? t('The check read "{{read}}" on this layer. Look it over, or change the text.', { read })
    : t('The automatic check could not read this text back. Look it over before you use it.');
};

export interface LayerListProps {
  /** The layers bottom to top (ascending `z`); listed top first, like a design tool's layer stack. */
  items: KitLayer[];
  selectedId?: string;
  /** A paid edit or the save is in flight. */
  disabled?: boolean;
  onSelect: (id: string) => void;
  onToggleHidden: (id: string) => void;
  onMove: (id: string, direction: 'forward' | 'backward') => void;
}

/**
 * The layer stack of the kit layer editor, top-most first: each row names the layer's role (and the model's own name
 * for it), and carries its visibility and bring forward / send backward controls. A text layer whose words did not read
 * back as intended (as separated, or after a rewrite or restyle) is flagged, its tooltip saying why
 * ({@link layerTextIssue}).
 */
export const LayerList = ({
  items,
  selectedId,
  disabled = false,
  onSelect,
  onToggleHidden,
  onMove,
}: LayerListProps): React.ReactElement => {
  const { t } = useTranslation();
  const topFirst = items.slice().reverse();
  return (
    <S.List aria-label={t('Layers')}>
      {topFirst.map((item, i) => {
        const role = layerRoleLabel(item.role, t);
        const hidden = item.transform.hidden;
        const isTop = i === 0;
        const isBottom = i === topFirst.length - 1;
        const issue = layerTextIssue(item.text, t);
        return (
          <S.Row key={item.id} $selected={item.id === selectedId}>
            <S.RowMain
              type="button"
              onClick={() => onSelect(item.id)}
              aria-pressed={item.id === selectedId}
              aria-label={t('Select the {{layer}} layer', { layer: role })}
            >
              <S.ControlRow>
                <S.RowRole raw text={role} $hidden={hidden} />
                {issue ? <S.CheckBadge text="Check the text" title={issue} /> : null}
              </S.ControlRow>
              {item.name && item.name !== item.role ? <S.RowName raw text={item.name} /> : null}
            </S.RowMain>
            <S.RowActions>
              <S.IconBtn
                type="button"
                disabled={disabled || isTop}
                onClick={() => {
                  if (disabled || isTop) return;
                  onMove(item.id, 'forward');
                }}
                aria-label={t('Bring {{layer}} forward', { layer: role })}
                title={t('Bring forward')}
              >
                <Icon name="chevron-up" size={16} />
              </S.IconBtn>
              <S.IconBtn
                type="button"
                disabled={disabled || isBottom}
                onClick={() => {
                  if (disabled || isBottom) return;
                  onMove(item.id, 'backward');
                }}
                aria-label={t('Send {{layer}} backward', { layer: role })}
                title={t('Send backward')}
              >
                <Icon name="chevron-down" size={16} />
              </S.IconBtn>
              <S.IconBtn
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  onToggleHidden(item.id);
                }}
                aria-pressed={hidden}
                aria-label={hidden ? t('Show {{layer}}', { layer: role }) : t('Hide {{layer}}', { layer: role })}
                title={hidden ? t('Show') : t('Hide')}
              >
                <Icon name={hidden ? 'eye-off' : 'eye'} size={16} />
              </S.IconBtn>
            </S.RowActions>
          </S.Row>
        );
      })}
    </S.List>
  );
};

export default LayerList;
